import { useMutation, useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { ScoutLaunchAnnouncementAction } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useRef, useState } from "react";

import { SCOUT_LAUNCH_URL } from "./scoutLaunchAnnouncementConfig";

const SCOUT_LAUNCH_ANNOUNCEMENT_STATUS = gql(`
  query ScoutLaunchAnnouncementStatus {
    scoutLaunchAnnouncementStatus {
      shouldShow
    }
  }
`);

const RECORD_SCOUT_LAUNCH_ANNOUNCEMENT_EVENT = gql(`
  mutation RecordScoutLaunchAnnouncementEvent($input: RecordScoutLaunchAnnouncementEventInput!) {
    recordScoutLaunchAnnouncementEvent(input: $input) {
      ok
    }
  }
`);

export type ScoutLaunchAnnouncementPhase = "idle" | "modal" | "tour";

export type UseScoutLaunchAnnouncementResult = {
  phase: ScoutLaunchAnnouncementPhase;
  tourStep: 1 | 2 | null;
  shouldShow: boolean;
  kbUrl: string;
  dismissModal: () => void;
  learnMore: () => void;
  startTour: () => void;
  completeTour: () => void;
  dismissTour: () => void;
  viewTourStep: (step: 1 | 2) => void;
};

export function useScoutLaunchAnnouncement(): UseScoutLaunchAnnouncementResult {
  const { tenantId } = useTenant();

  // Server still owns the per-user announcement state; local Scout access avoids
  // querying for users who cannot see Scout in the first place.
  const { data, error } = useQuery(SCOUT_LAUNCH_ANNOUNCEMENT_STATUS, {
    skip: !tenantId,
    fetchPolicy: "network-only",
    errorPolicy: "ignore",
    context: {
      tenantIdOverride: tenantId,
    },
  });

  const shouldShow =
    !error && tenantId != null && data?.scoutLaunchAnnouncementStatus.shouldShow === true;

  const [recordEvent] = useMutation(RECORD_SCOUT_LAUNCH_ANNOUNCEMENT_EVENT, {
    errorPolicy: "ignore",
    context: {
      tenantIdOverride: tenantId,
    },
  });
  const hasRecordedShownRef = useRef(false);
  const [phase, setPhase] = useState<ScoutLaunchAnnouncementPhase>("idle");
  const [tourStep, setTourStep] = useState<1 | 2 | null>(null);

  const record = useCallback(
    (action: ScoutLaunchAnnouncementAction, tourStepValue?: number) => {
      void recordEvent({
        variables: {
          input: {
            action,
            ...(tourStepValue !== undefined ? { tourStep: tourStepValue } : {}),
          },
        },
      });
    },
    [recordEvent]
  );

  useEffect(() => {
    if (!shouldShow || phase !== "idle") {
      return;
    }

    if (hasRecordedShownRef.current) {
      return;
    }

    hasRecordedShownRef.current = true;
    record(ScoutLaunchAnnouncementAction.Shown);
    setPhase("modal");
  }, [shouldShow, phase, record]);

  const dismissModal = useCallback(() => {
    record(ScoutLaunchAnnouncementAction.Dismissed);
    setPhase("idle");
  }, [record]);

  const learnMore = useCallback(() => {
    window.open(SCOUT_LAUNCH_URL, "_blank", "noopener,noreferrer");
    // Analytics only — keep the modal open so the user can return and start the tour.
    record(ScoutLaunchAnnouncementAction.LearnMoreClicked);
  }, [record]);

  const startTour = useCallback(() => {
    record(ScoutLaunchAnnouncementAction.TourStarted);
    setTourStep(1);
    setPhase("tour");
  }, [record]);

  const viewTourStep = useCallback(
    (step: 1 | 2) => {
      setTourStep(step);
      record(ScoutLaunchAnnouncementAction.TourStepViewed, step);
    },
    [record]
  );

  const completeTour = useCallback(() => {
    record(ScoutLaunchAnnouncementAction.TourCompleted);
    setTourStep(null);
    setPhase("idle");
  }, [record]);

  const dismissTour = useCallback(() => {
    record(ScoutLaunchAnnouncementAction.TourDismissed);
    setTourStep(null);
    setPhase("idle");
  }, [record]);

  return {
    phase,
    tourStep,
    shouldShow,
    kbUrl: SCOUT_LAUNCH_URL,
    dismissModal,
    learnMore,
    startTour,
    completeTour,
    dismissTour,
    viewTourStep,
  };
}
